// platform helpers
import _ from 'lodash';
import { useQuery } from '@tanstack/react-query';
import Moment from 'moment/moment';
import { useSelector } from 'react-redux';
// mui
import { Box, Card, Divider, Typography } from '@mui/material';
import { styled, makeStyles, useTheme } from '@mui/styles';
// json
import CoverageStatics from '../../../../../_statics/250/coverages/coverages.statics.json';
// apis
import UWComparisonCoverages from '../../../../../_tabs/uwComparisonDrawer/UWComparisonCoverages';
import { fetchP250Coverages } from '../../../../../../accounts/AccountService';
// utils
import { Flexbox } from '../policy-details.shared-components.utils';
import {
  getCoverageFormat,
  getCoverages,
  retroMappingShort,
} from '../../../../../_global/endorsements/endorsement.utils';
import {
  PrimeXProductTypes,
  ProductTypes,
  isPrimeX,
} from '../../../../../../types';
import { formatCurrencyToTenthsPlaceWithUnit } from '../../../../../../utils/currency.utils';
import { toUniversalUtcDate } from '../../../../../../utils/date.utils';
import { PolicyStatus } from '../../../../../_statics/policy.statics';
import { fetchLatestPolicyData } from '../../../../../../api/policies.api';
import { getData } from '../../../../../../utils/functional/fp';
import { useAPIErrorHandler } from '../../../../../../components/hooks/useAPIErrorHandler';
import PolicyCoverageDetailsResolver from './PolicyCoverageDetailsResolver';
import CoverageDetailsDataProcessor from '../../../../../agencies/quotes/PrimeX/quoteDetails/components/CoverageDetailsDataProcessor';
import TruncateTextWithTooltip from '../../../../../../utility/TruncateTextWithTooltip';
import { BoostRaterVersionResponse } from '../../../../../workflows/quotes/prime-100/api/types';
import { useP100RaterVersionFallback } from '../../../../../workflows/quotes/prime-100/hooks/useP100RaterVersionFallback';
import { calculateCoverageOptionsV6 } from '../../../../../_statics/coverage.statics.v6';
import { PRIME_100_COVERAGES } from '../../../../../workflows/quotes/prime-100/api/constants';
import { buildSubCoverageRows } from '../../../../../workflows/quotes/prime-100/utils';
import { moneyFormat } from '../../../../../../utils/appUtils';
import { ensureArray } from '../../../../../../utils';
import CheckBoxSelectedIcon from '../../../../../../_assets/svg/CheckBoxSelectedIcon.svg';

const isV6 = (version: string | undefined) =>
  typeof version === 'string' && version.toUpperCase() === 'V6';

const CHECKBOX_ONLY_COVERAGES = new Set([
  'ADDITIONAL_INSURED',
  'TRIA',
  'BLANKET_ADDITIONAL_INSURED',
  'ADDITIONAL_NAMED_INSURED',
  'CONTINGENT_BUSINESS_INCOME_SECURITY_FAILURE',
  'CONTINGENT_BUSINESS_INCOME_SYSTEM_FAILURE_IT',
  'ENHANCED_BUSINESS_AND_CONTINGENT_BUSINESS_INCOME',
  'ENHANCED_BUSINESS_AND_CONTINGENT_BUSINESS_INCOME_SYSTEM_FAILURE_INCLUSIVE',
]);

const V5_SELECTED_CHECKBOX_COVERAGES = new Set([
  'ENHANCED_BUSINESS_AND_CONTINGENT_BUSINESS_INCOME',
  'ENHANCED_BUSINESS_AND_CONTINGENT_BUSINESS_INCOME_SYSTEM_FAILURE_INCLUSIVE',
]);

const PRIME_100_COVERAGES_BY_ID = PRIME_100_COVERAGES.reduce<
  Record<string, { limitKey: string; deductibleKey: string }>
>((acc, cov) => {
  acc[cov.name] = { limitKey: cov.limitKey, deductibleKey: cov.deductibleKey };
  return acc;
}, {});

type V6CoverageContext = {
  coverageMapping: Record<string, unknown> | undefined;
  state?: string;
};

type CoverageOption = {
  id?: string;
  label?: string;
  checked?: boolean;
  defaultChecked?: boolean;
  mandatory?: boolean;
  percentage?: number;
};

function resolveNumericLimit(coverages: any): number {
  return typeof coverages?.limit === 'number' && coverages.limit > 0
    ? coverages.limit
    : Number(coverages?.limit) || 1_000_000;
}

function resolveOptionLimit(
  coverages: any,
  enumId: string,
  percentage: number | undefined,
  numericLimit: number
): string | null {
  const keys = PRIME_100_COVERAGES_BY_ID[enumId];
  const limitVal = keys?.limitKey ? coverages?.[keys.limitKey] : null;
  if (limitVal) return moneyFormat(limitVal);
  if (typeof percentage === 'number') {
    return moneyFormat(Math.round(percentage * numericLimit));
  }
  return null;
}

function classifyOption(enumId: string): 'checkbox-only' | 'regular' {
  return CHECKBOX_ONLY_COVERAGES.has(enumId) ? 'checkbox-only' : 'regular';
}

function buildP100V6Coverages(
  coverages: any,
  boostVersion: string | undefined,
  v6Context: V6CoverageContext | undefined
) {
  const options = calculateCoverageOptionsV6(coverages ?? {}, {
    onChange: null,
    register: null,
    coverageMapping: v6Context?.coverageMapping ?? {},
    state: v6Context?.state,
    boostVersion: boostVersion ?? BoostRaterVersionResponse.V6,
  }) as CoverageOption[];

  const numericLimit = resolveNumericLimit(coverages);

  const result: {
    coverageName: string;
    order: number;
    limit?: string | null;
    deductible?: string | null;
    checked?: boolean;
  }[] = [];

  options.forEach((option, index) => {
    const enumId = typeof option.id === 'string' ? option.id : '';

    if (!option.label) {
      result.push(...buildSubCoverageRows(coverages, enumId, index));
      return;
    }

    const isActive =
      option.mandatory || option.defaultChecked || option.checked;
    if (!isActive) return;

    if (classifyOption(enumId) === 'checkbox-only') {
      result.push({ coverageName: option.label, order: index, checked: true });
      return;
    }

    const keys = PRIME_100_COVERAGES_BY_ID[enumId];
    const deductibleVal = keys?.deductibleKey
      ? coverages?.[keys.deductibleKey]
      : null;

    const deductible =
      deductibleVal != null ? moneyFormat(deductibleVal) : null;
    result.push({
      coverageName: option.label,
      order: index,
      limit: resolveOptionLimit(
        coverages,
        enumId,
        option.percentage,
        numericLimit
      ),
      deductible,
    });
  });

  return result.sort((a, b) => a.order - b.order);
}

function toUWComparisonFormat(
  v6Coverages: {
    coverageName: string;
    order: number;
    limit?: string | null;
    deductible?: string | null;
    checked?: boolean;
  }[]
) {
  return v6Coverages.map((coverage) => {
    const deductible = coverage.deductible ?? '-';
    const rowEntry = coverage.checked
      ? { isChecked: true, waitingPeriod: '-' }
      : {
          limit: coverage.limit || '-',
          deductible,
          waitingPeriod: '-',
          retroActivePeriod: '-',
        };
    return {
      groupId: '1',
      groupName: coverage.coverageName,
      label: coverage.coverageName,
      row: [rowEntry],
    };
  });
}

/**
 * Reduce a P250 coverage list into a name-keyed object. Guards against a
 * non-array `latestCoverages` (previously crashed with "reduce is not a
 * function").
 */
export function coverageListToObject(
  coveragesArray: unknown
): Record<string, any> {
  return ensureArray<any>(coveragesArray).reduce<Record<string, any>>(
    (acc, coverage) => ({
      ...acc,
      [coverage.name]: { ...coverage },
    }),
    {}
  );
}

export const policyStatusForShowingDrawer = [
  PolicyStatus.ISSUED_STATUS,
  PolicyStatus.IN_FORCE_STATUS,
  PolicyStatus.RENEWED_STATUS,
  PolicyStatus.EXPIRED_STATUS,
];

export const PolicyCoverageDetails = ({
  policy,
  product: productType,
}: any) => {
  const fetchLatestPolicyByStatus = policyStatusForShowingDrawer.includes(
    policy.status
  );

  const isPrimeXProduct =
    Object.values(PrimeXProductTypes).includes(productType);

  const p100CoverageMapping = useSelector(
    (reduxState: { auth?: { P_100?: Record<string, unknown> } }) =>
      reduxState.auth?.P_100
  );

  const { version: boostVersion, isLoading: isBoostVersionLoading } =
    useP100RaterVersionFallback(
      productType === ProductTypes.p100 ? policy : undefined
    );

  const coverageDetails = useCoverageDetails({
    policy,
    productType,
    fetchLatestPolicyByStatus,
    boostVersion,
    p100CoverageMapping,
  });

  if (policy.raterXCompatible) {
    return <PolicyCoverageDetailsResolver policyDetails={policy} />;
  }

  if (
    !isPrimeXProduct &&
    productType === ProductTypes.p100 &&
    isBoostVersionLoading
  ) {
    return <CoverageDetailsCard />;
  }

  return (
    <CoverageDetailsCard>
      {isPrimeXProduct ? (
        <CoverageDetailsDataProcessor
          {...{
            effectiveDate: policy?.policy?.effectiveDate,
            productType,
            limit: policy?.limit,
            deductible: policy?.deductible,
            agencyId: policy?.quote?.agencyId,
            coverages: coverageDetails,
            accountData: policy.firmographicData,
          }}
          boxProps={{ pl: '1rem' }}
          containerProps={{
            elevation: 2,
            style: {
              marginLeft: '-1rem',
            },
          }}
        />
      ) : (
        <>
          <Flexbox p={1}>
            <Typography color="primary">Coverage Details and Limits</Typography>
            <Typography color="primary">
              (Including all Endorsements)
            </Typography>
          </Flexbox>
          <UWComparisonCoverages
            uwCompareCoverages={coverageDetails}
            customUWComparisonCoveragesRow={CustomUWComparisonCoveragesRow}
            customComparisonRowLabel={CustomComparisonRowLabel}
            customComparisonColumn={CustomComparisonColumn}
            customUWComparisonGroupHeader={CustomUWComparisonGroupHeader}
          />
        </>
      )}
    </CoverageDetailsCard>
  );
};

const useCoverageDetails = ({
  policy,
  productType,
  fetchLatestPolicyByStatus,
  boostVersion,
  p100CoverageMapping,
}: any) => {
  const handleAPIError = useAPIErrorHandler();

  const { data: p250Coverages } = useQuery({
    queryKey: ['p250-coverages'],
    queryFn: () => fetchP250Coverages().then(getData),
    enabled: productType === ProductTypes.p250,
    refetchOnWindowFocus: false,
    onError: handleAPIError('Error retrieving P250 coverages'),
  });

  const latestPolicyQuery = useQuery({
    queryKey: [
      'latest-policy',
      { product: productType, policyNumber: policy.policyNumber },
    ],
    queryFn: () =>
      fetchLatestPolicyData(productType, policy.policyNumber).then(getData),
    enabled: isPrimeX(productType),
    retry: false,
  });

  if (productType === ProductTypes.p100) {
    const isLatestCoveragesAvailable = Boolean(
      policy.latestCoverages?.coverages
    );

    const rawCoverages =
      !fetchLatestPolicyByStatus || !isLatestCoveragesAvailable
        ? getCoverages(policy, productType)?.coverages
        : policy?.latestCoverages?.coverages;

    if (isV6(boostVersion)) {
      const v6Results = buildP100V6Coverages(rawCoverages ?? {}, boostVersion, {
        coverageMapping: p100CoverageMapping,
        state: policy?.state,
      });
      return toUWComparisonFormat(v6Results);
    }

    const formattedCoverages = getCoverageFormat(rawCoverages);
    return constructFinalP100Coverages(formattedCoverages);
  }

  if (productType === ProductTypes.p250) {
    const policyCoveragesObject = coverageListToObject(policy.latestCoverages);
    return handleMergeData({
      policyCoveragesObject,
      p250Coverages,
    });
  }

  if (isPrimeX(productType)) {
    // latestPolicyQuery fails if no endorsements so we fallback to policy
    // @ts-expect-error TS(2339): Property 'coverages' does not exist on type 'Polic... Remove this comment to see the full error message
    return latestPolicyQuery.data?.coverages || policy.coverages;
  }
};

export const constructFinalP100Coverages = (coverages: any) => {
  return coverages.map((coverage: any) => {
    const row =
      V5_SELECTED_CHECKBOX_COVERAGES.has(coverage.key) && coverage.selected
        ? { isChecked: true, waitingPeriod: '-' }
        : {
            ..._.pick(coverage, ['limit', 'deductible', 'retroActivePeriod']),
            waitingPeriod: '-', // P100 doesn't have per coverage waiting
          };

    return {
      groupId: coverage.groupId,
      groupName: coverage.key,
      label: coverage.name,
      row: [row],
    };
  });
};

const handleMergeData = ({
  policyCoveragesObject = {},
  p250Coverages = [],
}) => {
  return CoverageStatics.filter(
    (stat) => policyCoveragesObject[stat.key]?.name
  ).map((stat) => {
    const { groupId } = stat;

    return {
      groupId,
      groupName: stat.key,
      label: p250Coverages[stat._id]?.name,
      row: [
        {
          limit: policyCoveragesObject[stat.key]?.limit
            ? formatCurrencyToTenthsPlaceWithUnit(
                +policyCoveragesObject[stat.key].limit
              )
            : '-',
          deductible: policyCoveragesObject[stat.key]?.deductible
            ? formatCurrencyToTenthsPlaceWithUnit(
                +policyCoveragesObject[stat.key].deductible
              )
            : '-',
          waitingPeriod: policyCoveragesObject[stat.key]?.waitingPeriod
            ? `${policyCoveragesObject[stat.key]?.waitingPeriod} Hrs`
            : '-',
          retroActivePeriod:
            policyCoveragesObject[stat.key]?.retroActivePeriod || '-',
          retroActiveDate:
            policyCoveragesObject[stat.key]?.retroActiveDate || '-',
        },
      ],
    };
  });
};

const CustomUWComparisonCoveragesRow = ({ rowData, index }: any) => {
  const { palette } = useTheme();
  const classes = useClasses();

  return (
    <div
      style={
        index % 2 === 0
          ? { background: palette.background.default, width: '100%' }
          : { background: palette.background.modal, width: '100%' }
      }
    >
      <Box
        display="flex"
        justifyContent="flex-start"
        alignItems="center"
        className={classes.container}
      >
        <CustomComparisonRowLabel rowData={rowData} />
        <Divider orientation="vertical" flexItem />
        {rowData.row.map((row: any, idx: any) => {
          return <CustomComparisonColumn rowData={row} index={idx} />;
        })}
      </Box>
    </div>
  );
};

const CustomComparisonRowLabel = ({ rowData }: any) => {
  return (
    <div
      style={{
        flex: '0 0 20rem',
        textAlign: 'right',
        fontSize: '1rem',
        overflow: 'hidden',
        padding: '0 0.5rem',
      }}
    >
      {rowData?.label && (
        <TruncateTextWithTooltip variant="caption" charLimit={40}>
          {rowData?.label}
        </TruncateTextWithTooltip>
      )}
    </div>
  );
};

const CustomComparisonColumn = ({ rowData }: any) => {
  const classes = useClasses();

  let retro = '-';
  if (
    rowData.retroActiveDate !== '-' &&
    rowData.retroActiveDate !== undefined
  ) {
    retro = toUniversalUtcDate(rowData.retroActiveDate);
  } else if (rowData.retroActivePeriod !== '-') {
    retro = retroMappingShort[rowData.retroActivePeriod];
  }

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      flex="1"
      style={{ position: 'relative' }}
      className={classes.innerContainer}
    >
      <div>{rowData.isChecked ? <CheckBoxSelectedIcon /> : rowData.limit}</div>
      <div>{rowData.deductible}</div>
      <div>{rowData.waitingPeriod}</div>
      <div>{retro}</div>
    </Box>
  );
};

const CustomUWComparisonGroupHeader = ({ sectionLabel }: any) => {
  const headerClasses = useHeaderClasses();
  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="flex-start"
      className={headerClasses.container}
    >
      <h3
        style={{ flex: '0 0 20rem', padding: '0 0.5rem', overflow: 'hidden' }}
        className={headerClasses.sectionTitle}
      >
        {sectionLabel}
      </h3>
      <Divider orientation="vertical" flexItem />
      <Box
        alignItems="center"
        display="flex"
        justifyContent="center"
        flex="1"
        className={headerClasses.innerContainer}
      >
        <div className={headerClasses.covLimit}>Coverage Limit</div>
        <div>Deductible</div>
        <div>Waiting Period</div>
        {/* @ts-expect-error TS(2339): Property 'borderedRightCell' does not exist on typ... Remove this comment to see the full error message */}
        <div className={headerClasses.borderedRightCell}>
          Retro Active Period
        </div>
      </Box>
    </Box>
  );
};

const CoverageDetailsCard = styled(Card)(({ theme: { palette } }) => ({
  marginTop: '1rem',
  backgroundColor: palette.background.default,
}));

const useClasses = makeStyles(() => ({
  container: {
    height: '2.8rem',
    '& > div': {
      fontSize: '0.75rem',
      lineHeight: '1.17',
    },
  },
  innerContainer: {
    gap: '2.5rem',
    '& > div': {
      minWidth: '3.5rem',
      textAlign: 'center',
      fontSize: '1rem',
    },
  },
  blueText: {
    color: '#2180e2 !important',
  },
  greenText: {
    color: '#6ac52a',
  },
}));

const useHeaderClasses = makeStyles(({ palette }) => ({
  container: {
    width: '100%',
    background: palette.background.paper,
    color: palette.text.secondary,
    textTransform: 'uppercase',
    borderTop: `solid 1px ${palette.primary.border}`,
    '& > div': {
      fontSize: '0.75rem',
      lineHeight: '1.17',
    },
  },
  innerContainer: {
    gap: '2.5rem',
    '& > div': {
      width: '3.5rem',
      textAlign: 'center',
      fontSize: '0.8rem',
    },
  },
  sectionTitle: {
    fontSize: '0.875rem',
    lineHeight: '1.43',
    fontWeight: '600',
    margin: '0',
    padding: '1rem 0',
    textAlign: 'right',
  },
  borderedCell: {
    borderLeft: `solid 1px ${palette.primary.border}`,
    borderRight: `solid 1px ${palette.primary.border}`,
    padding: '0 0.625rem',
  },
  covLimit: {
    textAlign: 'center',
    width: '25%',
  },
}));
